import { useState, useEffect } from 'react';
import { collection, addDoc, serverTimestamp, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Project, Proposal } from '../../models/types';
import { toast } from 'react-toastify';

interface ProposalFormProps {
  project: Project;
  existingProposal?: Proposal;
  onClose: () => void;
  onSuccess?: () => void;
}

const ProposalForm = ({ project, existingProposal, onClose, onSuccess }: ProposalFormProps) => {
  const { currentUser, userData } = useAuth();
  const { t } = useLanguage();
  const [message, setMessage] = useState(existingProposal?.message || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    console.log('ProposalForm initialized:', {
      projectId: project.id,
      projectName: project.name,
      currentUser: currentUser?.uid,
      userData: userData?.username,
      existingProposal: existingProposal?.id
    });
  }, [project, currentUser, userData, existingProposal]);

  const validateProposal = (message: string): boolean => {
    console.log('Validating proposal message:', { length: message.length, content: message.substring(0, 50) + '...' });
    
    if (!message.trim()) {
      setError(t('proposal.messageRequired'));
      console.error('Validation failed: Empty message');
      return false;
    }
    if (message.length < 50) {
      setError(t('proposal.minCharsError'));
      console.error('Validation failed: Message too short');
      return false;
    }
    if (message.length > 2500) {
      setError(t('proposal.maxCharsError'));
      console.error('Validation failed: Message too long');
      return false;
    }
    setError(null);
    console.log('Validation passed');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('=== PROPOSAL SUBMISSION STARTED ===');
    console.log('Form submitted with:', {
      message: message.substring(0, 100) + '...',
      messageLength: message.length,
      projectId: project.id,
      currentUser: currentUser?.uid,
      userData: userData
    });
    
    if (!currentUser || !userData) {
      console.error('No current user or user data');
      toast.error('Authentication error. Please log in again.');
      return;
    }

    if (!validateProposal(message)) {
      console.error('Validation failed');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      console.log('Starting proposal submission process...');

      if (existingProposal) {
        console.log('Updating existing proposal:', existingProposal.id);
        // Update existing proposal
        const proposalRef = doc(db, 'proposals', existingProposal.id);
        const proposalDoc = await getDoc(proposalRef);
        
        if (proposalDoc.exists()) {
          const currentData = proposalDoc.data();
          const updates = currentData.updates || [];
          
          await updateDoc(proposalRef, {
            message: message.trim(),
            updates: [
              ...updates,
              {
                message: currentData.message,
                createdAt: currentData.updatedAt || currentData.createdAt
              }
            ],
            updatedAt: serverTimestamp()
          });
          console.log('Proposal updated successfully');
          toast.success('Proposal updated successfully');
        }
      } else {
        console.log('Creating new proposal...');
        // Create new proposal
        const proposalData = {
          projectId: project.id,
          providerId: currentUser.uid,
          providerName: userData.username,
          message: message.trim(),
          status: 'pending',
          createdAt: serverTimestamp(),
          updates: []
        };
        
        console.log('Proposal data to be saved:', proposalData);
        
        // Add proposal to Firestore
        const docRef = await addDoc(collection(db, 'proposals'), proposalData);
        console.log('Proposal created with ID:', docRef.id);

        // Create notification for requester
        try {
          const notificationData = {
            userId: project.userId,
            title: 'New Proposal Received',
            message: `${userData.username} has submitted a proposal for "${project.name}"`,
            type: 'proposal',
            read: false,
            createdAt: serverTimestamp(),
            metadata: {
              projectId: project.id,
              proposalId: docRef.id
            }
          };
          
          console.log('Creating notification for requester:', notificationData);
          await addDoc(collection(db, 'notifications'), notificationData);
          console.log('Notification created successfully');
        } catch (notificationError) {
          console.warn('Could not create notification:', notificationError);
          // Don't fail the proposal submission if notification fails
        }

        toast.success('Proposal submitted successfully');
      }

      console.log('Proposal submission completed successfully');
      
      // Call success callback if provided
      if (onSuccess) {
        console.log('Calling onSuccess callback');
        onSuccess();
      }
      
      // Close the modal
      console.log('Closing proposal form...');
      onClose();
      
    } catch (error) {
      console.error('=== PROPOSAL SUBMISSION ERROR ===');
      console.error('Error details:', error);
      
      if (error instanceof Error) {
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
        
        if (error.message.includes('permission-denied')) {
          setError('Permission denied. Please check your subscription status.');
          toast.error('Permission denied. Please check your subscription status.');
        } else if (error.message.includes('network')) {
          setError('Network error. Please check your connection.');
          toast.error('Network error. Please check your connection.');
        } else {
          setError('Error submitting proposal. Please try again.');
          toast.error('Error submitting proposal. Please try again.');
        }
      } else {
        setError('Unknown error occurred. Please try again.');
        toast.error('Unknown error occurred. Please try again.');
      }
    } finally {
      setSubmitting(false);
      console.log('=== PROPOSAL SUBMISSION ENDED ===');
    }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
            {t('proposal.message')} *
          </label>
          <textarea
            id="message"
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              if (error) setError(null);
            }}
            rows={8}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600 resize-none ${
              error ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder={t('proposal.messageDesc')}
            maxLength={2500}
            disabled={submitting}
          />
          {error && (
            <p className="mt-1 text-sm text-red-600">{error}</p>
          )}
          <div className="mt-1 flex justify-between text-sm">
            <span className="text-gray-500">
              {t('proposal.minChars')} {message.length}
            </span>
            <span className={`${message.length > 2500 ? 'text-red-500' : 'text-gray-500'}`}>
              {message.length}/2500 {t('profile.characters')}
            </span>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            disabled={submitting}
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={submitting || !message.trim() || message.length < 50 || message.length > 2500}
          >
            {submitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {existingProposal ? t('proposal.updating') : t('proposal.submitting')}
              </>
            ) : (
              existingProposal ? t('proposal.update') : t('proposal.submit')
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProposalForm;